import React, { useEffect, useState } from "react";
import { Basic } from "react-dial-knob";
import { ChunkConfig } from "../../GratefulDB";
import { entryVenueCoverage } from "../shared/Helpers";
import LCDDisplay from "../shared/LCDDisplay";
import { AudioControls } from "../shared/useAudioControls";
import "./Radio.scss";

export const Radio: React.FC<{
  audioControls: AudioControls;
  chunkConfig: ChunkConfig;
}> = (props): JSX.Element => {
  const knobColor = process.env.REACT_APP_PRIMARY_COLOR;
  const knobStyle = { marginLeft: "auto", marginRight: "auto" };
  const knobTheme = {
    defaultColor: knobColor,
    activeColor: knobColor,
    notchAndText: knobColor,
  };
  const knobThemeError = {
    ...knobTheme,
    notchAndText: "#ff7276",
  };

  const [startYear, setStartYear] = useState<number>(
    props.chunkConfig.yearStart
  );
  const [endYear, setEndYear] = useState<number>(props.chunkConfig.yearEnd);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const getMetadata = () => {
    const entry = props.audioControls.getEntry();
    const track = props.audioControls.getTrack();
    if (props.audioControls.isRadio() && entry && track) {
      return {
        title: track.title,
        date: entry.date,
        venueCoverage: entryVenueCoverage(entry),
      };
    }
    return {
      title: "---",
      date: "---",
      venueCoverage: "---",
    };
  };

  const start = () => {
    props.audioControls.startRadio(startYear, endYear);
  };

  useEffect(() => {
    setIsDirty(startYear > endYear || endYear < startYear);
  }, [startYear, endYear]);

  const metadata = getMetadata();
  const playerState = props.audioControls.getPlayerState();

  const selectorGrid = (
    <div className="ui grid inlay knobs">
      <div className="eight wide mobile four wide tablet three wide computer center aligned column">
        <Basic
          diameter={100}
          min={props.chunkConfig.yearStart}
          max={props.chunkConfig.yearEnd}
          step={1}
          value={startYear}
          theme={isDirty ? knobThemeError : knobTheme}
          knobStyle={knobStyle}
          onValueChange={setStartYear}
        />
        <h3>START YEAR</h3>
      </div>
      <div className="eight wide mobile four wide tablet three wide computer center aligned column">
        <Basic
          diameter={100}
          min={props.chunkConfig.yearStart}
          max={props.chunkConfig.yearEnd}
          step={1}
          value={endYear}
          theme={isDirty ? knobThemeError : knobTheme}
          knobStyle={knobStyle}
          onValueChange={setEndYear}
        />
        <h3>END YEAR</h3>
      </div>
      <div className="four wide tablet seven wide computer tablet computer only column"></div>
      <div className="sixteen wide mobile four wide tablet three wide computer middle aligned center aligned column">
        <div
          className={`ui icon massive button ${isDirty ? "disabled" : ""}`}
          onClick={start}
        >
          <i className="icon play"></i>
          <h2>PLAY</h2>
        </div>
      </div>
    </div>
  );

  const nextEntryGrid = (
    <div className="ui grid inlay next">
      <div className="sixteen wide mobile two wide tablet two wide computer middle aligned center aligned column entry">
        <h2>NEXT TRACK</h2>
      </div>
      <div className="sixteen wide mobile eleven wide tablet eleven wide computer column entry next-entry">
        {playerState.metadata ? (
          <div>
            <h3>{playerState.metadata.next.track.title}</h3>
            <h3>
              {playerState.metadata.next.entry.date}
              <br />
              {entryVenueCoverage(playerState.metadata.next.entry)}
            </h3>
          </div>
        ) : null}
      </div>
      <div className="sixteen wide mobile two wide tablet three wide computer middle aligned center aligned column">
        <div
          className="ui icon large button"
          onClick={props.audioControls.nextTrack}
        >
          <i className="icon step forward"></i>
          <h2>NEXT</h2>
        </div>
      </div>
    </div>
  );

  return (
    <div className="Radio">
      <div className="ui container">
        <div className="ui grid">
          <div className="sixteen wide mobile eight wide tablet eight wide computer column">
            <h2>
              <i className="compact disc icon" /> Virtual Radio
            </h2>
          </div>
          <div className="sixteen wide mobile eight wide tablet eight wide computer bottom aligned right aligned column tagline">
            <i>Let the algorithm decide, only soundboards, no repeats.</i>
          </div>
          <div className="sixteen wide column">
            <LCDDisplay
              title={metadata.title}
              date={metadata.date}
              venueCoverage={metadata.venueCoverage}
              marquee={true}
            />
            {!props.audioControls.isRadio() ? selectorGrid : nextEntryGrid}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Radio;
